export interface BaseProduct {
  isEligible: boolean;
  productBrochure: string;
  carrierBrochure: string;
  isDiscountingAvailable: boolean;
  productId?: number;
  carrierMmpDigitalPlacematUrl?: string;
  productAlias?: string;
  productAliasShortForm?: string;
  /**
   * User-facing display name resolved once at fetch time. Prefers the
   * short-form alias, falling back to the static eProductDisplayNames value.
   */
  displayName?: string;
  /**
   * Full-form display name resolved once at fetch time. Prefers the full
   * alias, falling back to the static eProductDisplayNames value. Used where
   * the full alias is required (e.g. the MMP comparison document).
   */
  displayNameFull?: string;
}

export interface Mmp extends BaseProduct { }
export interface Ppm extends BaseProduct { }
export interface KeyProtection extends BaseProduct { }
export interface Gap extends BaseProduct {
  isAllowEVGAP: boolean;
}
export interface Pep extends BaseProduct { }
export interface TireWheel extends BaseProduct { }
export interface Rsa extends BaseProduct { }
export interface Lpt extends BaseProduct { }
export interface VehicleReturn extends BaseProduct { }
export interface Ppi extends BaseProduct { }
export interface PaintlessDent extends BaseProduct { }
export interface Ersa extends BaseProduct { }
export interface PowerSports extends BaseProduct { }
export interface Clcd extends BaseProduct { }
export interface Sd extends BaseProduct { }
export interface Cpi extends BaseProduct { }
export interface Cvp extends BaseProduct { }

export interface IFiProducts {
  message: string;
  mmp: Mmp;
  ppm: Ppm;
  keyProtection: KeyProtection;
  gap: Gap;
  pep: Pep;
  sd: Sd;
  tireWheel: TireWheel;
  rsa: Rsa;
  lpt: Lpt;
  vehicleReturn: VehicleReturn;
  ppi: Ppi;
  paintlessDent: PaintlessDent;
  ersa: Ersa;
  powerSports: PowerSports;
  clcd: Clcd;
  cpi: Cpi;
  cvp: Cvp;
}

// Use this to check for available products on Account.
export enum eAvailableProducts {
  GAP = 'gap',
  PEP = 'pep',
  MMP = 'mmp',
  VR = 'vehicleReturn',
  PPM = 'ppm',
  KP = 'keyProtection',
  PPI = 'ppi',
  CVP = 'cvp',
  SD = 'sd'
}

export enum eProductRateNames {
  gap = 'Gap',
  pep = 'Pep',
  mmp = 'Mmp',
  vehicleReturn = 'Vr',
  ppm = 'Ppm',
  keyProtection = 'KeyProtection',
  paymentProtection = 'PaymentProtection',
  cvp = 'Cvp',
  sd = 'Sd'
}

export enum eBundleProductNames {
  MMP = 'MMP',
  VEHICLERETURN = 'VR',
  PPM = 'PPM',
  KEYPROTECTION = 'KP',
  GAP = 'GAP',
  PEP = 'PEP',
  PPI = 'PPI',
  SD = 'SD',
  CVP = 'CVP'
}

export enum eBundleRequestProductNames {
  MMP = 'MMP',
  VR = 'VEHICLERETURN',
  PPM = 'PPM',
  KP = 'KEYPROTECTION',
  GAP = 'GAP',
  PEP = 'PEP',
  PPI = 'PPI'
}

// Use this for all other use cases other than checking for available products on Account.
export enum eProductNames {
  MMP = 'mmp',
  VR = 'vehicleReturn',
  PPM = 'ppm',
  KP = 'keyProtection',
  GAP = 'gap',
  PEP = 'pep',
  SD = 'sd',
  PPI = 'paymentProtection',
  CVP = 'cvp'
}

export enum eProductDisplayNames {
  mmp = 'MMP',
  powerSports = 'PowerSports',
  vehicleReturn = 'Vehicle Return',
  ppm = 'Maintenance Benefit Agreement',
  keyProtection = 'Key Protection',
  gap = 'GAP',
  pep = 'PEP',
  ppi = 'Payment Protection',
  sd = 'Screen Protection',
  paymentProtection = 'Payment Protection',
  cvp = 'CVP'
}

/**
 * Stamps each product with its resolved display names once, at fetch time.
 *
 * When the /Products response includes an alias for a product (currently used
 * for MMP), that alias overrides the static `eProductDisplayNames` value so the
 * alias is shown across the app.
 *
 * - `displayName` prefers the short-form alias (used everywhere by default).
 * - `displayNameFull` prefers the full alias (used where the full name is
 *   required, e.g. the MMP comparison document).
 *
 * Both fall back to the static `eProductDisplayNames` value, then the key.
 * The payload is mutated in place.
 *
 * @param fiProducts Products payload from the /Products call.
 */
export function patchProductDisplayNames(fiProducts?: IFiProducts): void {
  if (!fiProducts) {
    return;
  }

  const products = fiProducts as unknown as FIProd;
  for (const productKey of Object.keys(products)) {
    const product = products[productKey];
    if (!product || typeof product !== 'object') {
      continue;
    }

    const fallback = eProductDisplayNames[productKey] ?? productKey;
    product.displayName = product.productAliasShortForm || fallback;
    product.displayNameFull = product.productAlias || fallback;
  }
}

export enum eProductNamesFromRateNames {
  mmpRate = 'mmp',
  vehicleReturnRate = 'vehicleReturn',
  ppmRate = 'ppm',
  keyProtectionRate = 'keyProtection',
  gapRate = 'gap',
  pepRate = 'pep',
  paymentProtectionRate = 'paymentProtection',
  cvpRate = 'cvp',
  sdRate = 'sd'
}
export interface ProductFormValues {
  gap: any;
  pep: any;
  mmp: any;
  vehicleReturn: any;
  ppm: any;
  keyProtection: any;
  sd: any;
  paymentProtection: any;
  cvp: any;
}

export type FIProd = { [key: string]: BaseProduct };
